import { useState } from 'react';
import { ListCheck } from 'lucide-react';

import { formatDateInput } from '../../utils/formatter';

const TaskScheduler = ({ task: localTask, onClose, onSave }) => {
  const [newStartDate, setNewStartDate] = useState(localTask.startBy);
  const [newEndDate, setNewEndDate] = useState(localTask.endBy);
  const [validationErrors, setValidationErrors] = useState({
    startBy: false,
    endBy: false,
    emptyStartDate: false,
    emptyEndDate: false
  });

  const handleChange = (e) => {
    e.preventDefault();

    if (e.target.name === 'startBy') {
      setNewStartDate(e.target.value);

      let startByError = false;
      let clearEndByError = false;
      if (e.target.value && newEndDate && (e.target.value > newEndDate)) {
        startByError = true;
      } else if (e.target.value && newEndDate && (e.target.value < newEndDate)) {
        clearEndByError = true;
      }

      // Clear validation errors on change
      setValidationErrors(prev => ({
        ...prev,
        startBy: startByError,
        endBy: clearEndByError ? false : prev.endBy,
        emptyStartDate: !e.target.value
      }));
    } else if (e.target.name === 'endBy') {
      setNewEndDate(e.target.value);

      let endByError = false;
      let clearStartByError = false;
      if (e.target.value && newStartDate && (e.target.value < newStartDate)) {
        endByError = true;
      } else if (e.target.value && newStartDate && (e.target.value > newStartDate)) {
        clearStartByError = true;
      }

      // Clear validation errors on change
      setValidationErrors(prev => ({
        ...prev,
        startBy: clearStartByError ? false : prev.startBy,
        endBy: endByError,
        emptyEndDate: !e.target.value
      }));
    }
  }

  const validateDates = () => {
    const start = new Date(newStartDate);
    const end = new Date(newEndDate);
    
    const newErrors = {
      startBy: start > end,
      endBy: end < start,
      emptyStartDate: !newStartDate,
      emptyEndDate: !newEndDate
    };
    
    setValidationErrors(newErrors);
    return !newErrors.startBy && !newErrors.endBy;
  }

  const handleOnSave = () => {
    if (validateDates()) {
      if (!newStartDate) {
        setNewStartDate(newEndDate);
      }
      if (!newEndDate) {
        setNewEndDate(newStartDate);
      }
  
      onSave(newStartDate, newEndDate);
    }
  }

  const getInputClassName = (fieldName) => {
    const baseClasses = "w-full px-3 py-2 border rounded-lg text-sm focus:ring-0";
    return `${baseClasses} ${
      validationErrors[fieldName]
        ? "border-red-500 focus:border-red-500"
        : "border-gray-200 focus:border-blue-500"
    }`;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-1/3">
        <p className="text-base font-semibold text-gray-600 mb-4">Task Scheduler</p>

        <div className="flex items-center mb-4">
          <h2 className="flex items-start align-top text-lg font-bold">
            <ListCheck size={20} className="mt-1 mr-2" />
            {localTask.title}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Start By</label>
            <input
              type="date"
              name="startBy"
              value={formatDateInput(newStartDate)}
              onChange={handleChange}
              data-amp-track-section="Task Scheduler"
              data-amp-track-action="Start_Date_Change"
              data-amp-track-description="Start Date Input Change"
              className={getInputClassName('startBy')}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">End By</label>
            <input
              type="date"
              name="endBy"
              value={formatDateInput(newEndDate)}
              onChange={handleChange}
              data-amp-track-section="Task Scheduler"
              data-amp-track-action="End_Date_Change"
              data-amp-track-description="End Date Input Change"
              className={getInputClassName('endBy')}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
        {(validationErrors.endBy || validationErrors.startBy) && (
          <div className="mt-1 text-sm text-red-500">
            <p className="mt-1 text-sm text-red-500">End date cannot be before Start date</p>
          </div>
        )}
        {(validationErrors.emptyStartDate && !validationErrors.emptyEndDate) && (
          <div className="mt-1 text-sm text-gray-700">
            <p className="mt-1 text-sm text-gray-700">When start date is empty, it will be set to the end date</p>
          </div>
        )}
        {(!validationErrors.emptyStartDate && validationErrors.emptyEndDate) && (
          <div className="mt-1 text-sm text-gray-700">
            <p className="mt-1 text-sm text-gray-700">When end date is empty, it will be set to the start date</p>
          </div>
        )}
        {(validationErrors.emptyStartDate && validationErrors.emptyEndDate) && (
          <div className="mt-1 text-sm text-red-500">
            <p className="mt-1 text-sm text-red-500">You cannot set both start and end date to empty</p>
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            data-amp-track-section="Task Scheduler"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Task Reschedule Modal"
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleOnSave}
            data-amp-track-section="Task Scheduler"
            data-amp-track-action="Save_Click"
            data-amp-track-description="Save Task Reschedule"
            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default TaskScheduler;